import { useState, useEffect, useCallback, useMemo } from "react";
import "./JobSpecComparison.css";
import { getSharedSelection, setSharedSelection } from "./sharedSelection";
import {
  getVerticalStacks,
  getSubStacks,
  getJobTitles,
  getJobDescriptionCompareRoles,
} from "./api";

/**
 * JobSpecComparison
 *
 * React port of the Angular job-spec-comparison.component.ts/.html.
 * Same rules and logic as the source, only look & feel changed to the new
 * Minerva shell.
 *
 * Selector: SINGLE-select cascade (Core Vertical Stack -> Sub Stack ->
 * Job Title), NOT multiselect - confirmed against the source's
 * <app-dropdown-section [showExportBtn]="false">, which uses single-pick
 * <app-dropdown> throughout. Picking a Job Title drives the SQL's
 * job_level +/-1 ladder server-side; there is no separate "pick 3 roles"
 * UI step.
 *
 * AI content (ai_job_overall_summary + the per-field ai_* columns) is wired
 * as-is here - the AI-summary redesign / new prompt is a separate future
 * task. For now this renders whatever is already stored, matching old
 * exactly.
 *
 * The old "Skill Set Matrix" summary card (skillsMatrixInfo1/2/3,
 * nonTechData1/2/3) is commented out in the source .html and is NOT
 * rendered here either - it was never a real screen, just dead code
 * carried in the .ts.
 */
export default function JobSpecComparison() {
  const [verticalStacks, setVerticalStacks] = useState([]);
  const [subStacks, setSubStacks] = useState([]);
  const [jobTitles, setJobTitles] = useState([]);

  const [selectedVertical, setSelectedVertical] = useState(null);
  const [selectedSubStack, setSelectedSubStack] = useState(null);
  const [selectedJobTitle, setSelectedJobTitle] = useState(null);

  const [loading, setLoading] = useState(false);
  const [showAIContent, setShowAIContent] = useState(true);

  // model1 = role below (level_offset -1), model2 = selected role (0),
  // model3 = role above (1) - matches the source's id1/id2/id3 ordering,
  // which relies on the SQL's ORDER BY level_offset ASC.
  const [model1, setModel1] = useState({});
  const [model2, setModel2] = useState({});
  const [model3, setModel3] = useState({});

  useEffect(() => {
    getVerticalStacks().then(setVerticalStacks).catch(() => setVerticalStacks([]));
  }, []);

  // Hydrates the initial selection from the Comp page's active role, shared
  // via localStorage 'stacksModel' (same key/shape as old Angular's
  // Config.stackModel - see App.js's write-side effect and stack-model.ts).
  // This is what makes "select Finance Manager on Comp, then open JD·Skills"
  // land straight on Finance Manager's ladder, matching old Minerva's tab
  // behavior (18/19-Aug discussion). Self-contained (fetches its own
  // vertical/sub-stack/job-title chain rather than depending on the other
  // effects' timing) - mirrors old's own loadJobTitles()/
  // loadDataForAllThree(). Mount-only: must not re-fire on a later manual
  // dropdown pick here, or it would keep fighting the user's own selection.
  useEffect(() => {
    let cancelled = false;
    const raw = getSharedSelection() || {};
    if (raw.jobTitleId == null) return;

    (async () => {
      try {
        const verticals = await getVerticalStacks();
        if (cancelled) return;
        const vertical = verticals.find((v) => v.id === raw.verticalStackId);
        if (!vertical) return;
        setSelectedVertical(vertical);

        const subs = await getSubStacks(vertical.id);
        if (cancelled) return;
        const sub = subs.find((s) => s.id === raw.subStackId);
        if (!sub) return;
        setSelectedSubStack(sub);

        const titles = await getJobTitles(sub.id);
        if (cancelled) return;
        const title = titles.find((t) => t.id === raw.jobTitleId);
        if (title) setSelectedJobTitle(title);
      } catch {
        // Hydration is a convenience - on any failure, fall back silently to
        // the normal empty-cascade state the page already handles.
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!selectedVertical) {
      setSubStacks([]);
      return;
    }
    getSubStacks(selectedVertical.id).then(setSubStacks).catch(() => setSubStacks([]));
  }, [selectedVertical]);

  useEffect(() => {
    if (!selectedSubStack) {
      setJobTitles([]);
      return;
    }
    // Seniority-first ordering (ADDED 2026-08-20): old's shared
    // dropdown-section sorts every job-title list jobLevel DESC (line 204),
    // and old's JD page uses that same component - matches FilterBar.jsx's
    // identical sort on the comp page. id ASC pins ties.
    getJobTitles(selectedSubStack.id)
      .then((rows) => setJobTitles(
        (rows || []).slice().sort((a, b) => ((b.job_level ?? 0) - (a.job_level ?? 0)) || (a.id - b.id))
      ))
      .catch(() => setJobTitles([]));
  }, [selectedSubStack]);

  // TWO-WAY SYNC (ADDED 2026-09-18, per agreed flow rule): picking a role
  // HERE now writes it back to the shared selection, tagged source:'jd', so
  // returning to Comp restores it (FilterBar restores ONLY jd-tagged
  // selections - see its initial-load effect - which is what keeps the old
  // removed restore's flash/Clear-Core-Stack problems from coming back).
  // Runs on every selectedJobTitle change, including the mount hydration
  // above - harmless there, it just writes back the same role Comp already
  // had, without the 'jd' consequences since FilterBar consumes the tag.
  useEffect(() => {
    if (!selectedJobTitle) return;
    setSharedSelection({
      verticalStackId: selectedVertical?.id ?? null,
      subStackId: selectedSubStack?.id ?? null,
      jobTitleId: selectedJobTitle.id,
      source: "jd",
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedJobTitle]);

  const handleVerticalChange = useCallback((opt) => {
    setSelectedVertical(opt);
    setSelectedSubStack(null);
    setSelectedJobTitle(null);
  }, []);

  const handleSubStackChange = useCallback((opt) => {
    setSelectedSubStack(opt);
    setSelectedJobTitle(null);
  }, []);

  // --- ported verbatim from job-spec-comparison.component.ts ---

  function parseRawDataToSections(rawText) {
    if (!rawText) return [];

    const lines = rawText.split(/\r?\n/).map((line) => line.trim()).filter((line) => line);
    const parsedSections = [];
    let current = null;
    let inSection = false;
    const introLines = [];

    for (const line of lines) {
      if (line.endsWith(":")) {
        inSection = true;
        current = { title: line.slice(0, -1).trim(), points: [], show: true };
        parsedSections.push(current);
      } else if (/^[•\-–●]/.test(line)) {
        if (current) {
          current.points.push({ text: line.replace(/^[•\-–●]\s*/, "").trim() });
        } else {
          current = { title: "", points: [{ text: line.replace(/^[•\-–●]\s*/, "").trim() }] };
          parsedSections.push(current);
        }
        inSection = true;
      } else if (!inSection) {
        introLines.push(line);
      }
    }

    if (introLines.length) {
      parsedSections.unshift({ title: "Summary", points: [{ text: introLines.join(" ") }], show: true });
    }

    return parsedSections;
  }

  function markCommonPointsThreeWay(data1, data2, data3) {
    const allPoints1 = data1.flatMap((s) => s.points.map((p) => (typeof p === "string" ? p : p.text)?.trim().toLowerCase()));
    const allPoints2 = data2.flatMap((s) => s.points.map((p) => (typeof p === "string" ? p : p.text)?.trim().toLowerCase()));
    const allPoints3 = data3.flatMap((s) => s.points.map((p) => (typeof p === "string" ? p : p.text)?.trim().toLowerCase()));

    const commonPoints = new Set(allPoints1.filter((p) => allPoints2.includes(p) && allPoints3.includes(p)));

    const titles1 = data1.map((s) => (s.title || "").trim().toLowerCase());
    const titles2 = data2.map((s) => (s.title || "").trim().toLowerCase());
    const titles3 = data3.map((s) => (s.title || "").trim().toLowerCase());
    const commonTitles = new Set(titles1.filter((t) => titles2.includes(t) && titles3.includes(t)));

    const updateSectionPoints = (sectionArray) => {
      for (const section of sectionArray) {
        section.isCommonTitle = commonTitles.has((section.title || "").trim().toLowerCase());
        section.points = section.points.map((p) => {
          const text = typeof p === "string" ? p : p.text;
          return {
            ...(typeof p === "string" ? { text } : p),
            isCommon: commonPoints.has(text?.trim().toLowerCase()),
          };
        });
      }
    };

    updateSectionPoints(data1);
    updateSectionPoints(data2);
    updateSectionPoints(data3);
  }

  function escapeHtml(text) {
    return text.replace(/[&<>"']/g, (m) => ({
      "&": "&amp;",
      "<": "&lt;",
      ">": "&gt;",
      '"': "&quot;",
      "'": "&#039;",
    }[m]));
  }

  function getFormattedAISummary(summary, highlightTitles = []) {
    if (!summary) return "No data found";
    const normalizeTitle = (title) => (title ? title.replace(/\s*\([^)]*\)/g, "").replace(/\s+/g, " ").trim().toLowerCase() : "");
    const extractParen = (title) => {
      const match = title.match(/\(([^)]*)\)/);
      return match ? match[0] : "";
    };
    let patterns = [];
    highlightTitles.filter(Boolean).forEach((title) => {
      const norm = normalizeTitle(title);
      if (!norm) return;
      const paren = extractParen(title);
      if (paren) patterns.push(`${norm} ${paren}`.trim());
      patterns.push(norm);
    });
    patterns = Array.from(new Set(patterns)).sort((a, b) => b.length - a.length);

    const boldJobTitles = (line) => {
      if (!patterns.length || !line) return line;
      let matches = [];
      patterns.forEach((pattern) => {
        if (!pattern) return;
        const regex = new RegExp(`\\b${pattern.replace(/[-/\\^$*+?.()|[\]{}]/g, "\\$&")}(?:\\s*\\([^)]*\\))?\\b`, "gi");
        let match;
        while ((match = regex.exec(line)) !== null) {
          matches.push({ start: match.index, end: match.index + match[0].length });
        }
      });
      matches = matches.sort((a, b) => a.start - b.start);
      const merged = [];
      for (const m of matches) {
        if (!merged.length || m.start > merged[merged.length - 1].end) {
          merged.push({ ...m });
        } else {
          merged[merged.length - 1].end = Math.max(merged[merged.length - 1].end, m.end);
        }
      }
      let result = line;
      for (let i = merged.length - 1; i >= 0; i--) {
        const { start, end } = merged[i];
        result = result.slice(0, end) + "</span>" + result.slice(end);
        result = result.slice(0, start) + '<span class="fw-bold">' + result.slice(start);
      }
      return result;
    };

    const lines = summary.split("\n");
    const resultLines = [];
    let inShortFound = false;

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];

      if (!inShortFound && line.trim().toLowerCase().startsWith("in short")) {
        inShortFound = true;
        resultLines.push(`<span class="fw-bold">${escapeHtml(line.trim())}</span>`);
        continue;
      }

      if (inShortFound && line.includes("→")) {
        const match = line.match(/^(.+?→.+?):(.*)$/);
        if (match) {
          const roleTransition = match[1].trim();
          const description = match[2].trim();
          resultLines.push(`<span class="fw-bold">${escapeHtml(roleTransition)}</span>: ${boldJobTitles(escapeHtml(description))}`);
          continue;
        }
      }

      resultLines.push(boldJobTitles(escapeHtml(line)));
    }

    return resultLines.join("<br>");
  }

  // --- fetch + derive, mirrors loadDataForAllThree() ---

  useEffect(() => {
    if (!selectedJobTitle) {
      setModel1({});
      setModel2({});
      setModel3({});
      return;
    }

    let cancelled = false;
    setLoading(true);

    getJobDescriptionCompareRoles(selectedJobTitle.id)
      .then((rows) => {
        if (cancelled) return;
        const byOffset = Object.fromEntries((rows || []).map((r) => [r.level_offset, r]));
        const m1 = byOffset[-1] || {};
        const m2 = byOffset[0] || {};
        const m3 = byOffset[1] || {};

        const kr1 = parseRawDataToSections(m1.key_responsibilities);
        const kr2 = parseRawDataToSections(m2.key_responsibilities);
        const kr3 = parseRawDataToSections(m3.key_responsibilities);
        const ks1 = parseRawDataToSections(m1.key_skills_and_competencies);
        const ks2 = parseRawDataToSections(m2.key_skills_and_competencies);
        const ks3 = parseRawDataToSections(m3.key_skills_and_competencies);
        const kpi1 = parseRawDataToSections(m1.kpi);
        const kpi2 = parseRawDataToSections(m2.kpi);
        const kpi3 = parseRawDataToSections(m3.kpi);

        // Common-point highlighting runs on RAW fields only - the AI-field
        // version is commented out in the source .ts. Preserved as-is.
        markCommonPointsThreeWay(kpi1, kpi2, kpi3);
        markCommonPointsThreeWay(kr1, kr2, kr3);
        markCommonPointsThreeWay(ks1, ks2, ks3);

        setModel1({
          ...m1,
          keyResponsibilities: kr1,
          keySkillsAndCompetencies: ks1,
          kpiSections: kpi1,
          aiKeyResponsibilities: parseRawDataToSections(m1.ai_key_responsibilities),
          aiKeySkillsAndCompetencies: parseRawDataToSections(m1.ai_key_skills_and_competencies),
          aiKpiSections: parseRawDataToSections(m1.ai_kpi),
        });
        setModel2({
          ...m2,
          keyResponsibilities: kr2,
          keySkillsAndCompetencies: ks2,
          kpiSections: kpi2,
          aiKeyResponsibilities: parseRawDataToSections(m2.ai_key_responsibilities),
          aiKeySkillsAndCompetencies: parseRawDataToSections(m2.ai_key_skills_and_competencies),
          aiKpiSections: parseRawDataToSections(m2.ai_kpi),
        });
        setModel3({
          ...m3,
          keyResponsibilities: kr3,
          keySkillsAndCompetencies: ks3,
          kpiSections: kpi3,
          aiKeyResponsibilities: parseRawDataToSections(m3.ai_key_responsibilities),
          aiKeySkillsAndCompetencies: parseRawDataToSections(m3.ai_key_skills_and_competencies),
          aiKpiSections: parseRawDataToSections(m3.ai_kpi),
        });
      })
      .finally(() => !cancelled && setLoading(false));

    return () => {
      cancelled = true;
    };
  }, [selectedJobTitle]);

  const formattedAISummary = useMemo(
    () =>
      getFormattedAISummary(model2?.ai_job_overall_summary, [
        model1?.minerva_job_title,
        model2?.minerva_job_title,
        model3?.minerva_job_title,
      ]),
    // getFormattedAISummary is a pure helper recreated each render; its real
    // inputs (model1/2/3) are already listed, so this is safe to omit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [model1, model2, model3]
  );

  function SectionList({ sections, showAI }) {
    if (!sections || sections.length === 0) {
      return (
        <div className="jsc-empty">
          <div className="jsc-empty-dot" />
          <div className="jsc-empty-text">No Data found</div>
        </div>
      );
    }
    return (
      <div>
        {sections.map((section, i) => (
          <div key={i}>
            {!showAI && section.title && <div className="section-title">{section.title}</div>}
            <ul className={showAI ? "jsc-list-plain" : "jsc-list-indent"}>
              {section.points.map((point, j) => (
                <li key={j} className={showAI ? "jsc-li-plain" : ""}>
                  <span className={point?.isCommon ? "jsc-common" : ""}>{point.text || point}</span>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="jsc-wrap">
      <div className="jsc-selector-row">
        <SingleDropdown label="Core Vertical" value={selectedVertical} placeholder="Select vertical stack" options={verticalStacks} getLabel={(o) => o.name} onChange={handleVerticalChange} />
        <span className="jsc-sep">»</span>
        <SingleDropdown label="Sub Stack" value={selectedSubStack} placeholder="Select any option" options={subStacks} getLabel={(o) => o.name} onChange={handleSubStackChange} disabled={!selectedVertical} />
        <span className="jsc-sep">»</span>
        <SingleDropdown label="Job Title" value={selectedJobTitle} placeholder="Select any option" options={jobTitles} getLabel={(o) => o.name} onChange={setSelectedJobTitle} disabled={!selectedSubStack} />
      </div>

      {!selectedJobTitle && (
        <div className="jsc-empty jsc-empty-page">
          <div className="jsc-empty-text">Select a job title to compare it against the role below and above it.</div>
        </div>
      )}

      {selectedJobTitle && (
        <div className={`jsc-panel${loading ? " jsc-loading" : ""}`}>
          <div className="jsc-panel-head">
            <h3 className="jsc-panel-title">Job Spec Description</h3>
            <button type="button" className="jsc-toggle-btn" onClick={() => setShowAIContent((v) => !v)}>
              {/* Icon matches the button's LABEL (what you switch to):
                  "Show Description" = human-written spec = person icon,
                  "Show AI Summary" = robot icon. Was inverted. */}
              {showAIContent ? <UserIcon /> : <RobotIcon />}
              {showAIContent ? "Show Description" : "Show AI Summary"}
            </button>
          </div>

          {/* Sub-heading naming the mode you are looking at - ported from
              old's job-spec-comparison.component.html:25
              (`{{ showAIContent ? 'AI Summary' : 'Job Description' }}`),
              which this port had dropped. */}
          <h4 className="jsc-mode-heading">{showAIContent ? "AI Summary" : "Job Description"}</h4>

          {showAIContent && (
            <table className="jsc-table jsc-summary-table">
              <tbody>
                <tr>
                  <td colSpan={3} dangerouslySetInnerHTML={{ __html: formattedAISummary }} />
                </tr>
              </tbody>
            </table>
          )}

          <table className="jsc-table">
            <thead>
              <tr>
                <th>Specification</th>
                <th><div className="jsc-role-sub">Role Below</div><div>{model1?.minerva_job_title || "Job Title 1"}</div></th>
                <th><div className="jsc-role-sub">Selected Role</div><div>{model2?.minerva_job_title || "Job Title 2"}</div></th>
                <th><div className="jsc-role-sub">Role Above</div><div>{model3?.minerva_job_title || "Job Title 3"}</div></th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Alternate Job Title</td>
                <td>{model1?.source_job_title_alternative || "No data found"}</td>
                <td>{model2?.source_job_title_alternative || "No data found"}</td>
                <td>{model3?.source_job_title_alternative || "No data found"}</td>
              </tr>
              <tr>
                <td>Reporting To</td>
                <td>{model1?.reporting_to || "No data found"}</td>
                <td>{model2?.reporting_to || "No data found"}</td>
                <td>{model3?.reporting_to || "No data found"}</td>
              </tr>
              <tr>
                <td>Experience</td>
                <td>{(showAIContent ? model1?.ai_experience : model1?.experience) || "No data found"}</td>
                <td>{(showAIContent ? model2?.ai_experience : model2?.experience) || "No data found"}</td>
                <td>{(showAIContent ? model3?.ai_experience : model3?.experience) || "No data found"}</td>
              </tr>
              {/* Job Summary / Key Responsibilities / Key Skills, and the whole
                  separate KPI table below, are the raw Description view's
                  tables - AI Summary mode already covers this ground in its
                  one prose paragraph above, so these are hidden there
                  (18-Aug call: AI Summary should show just the paragraph +
                  Alternate Job Title/Reporting To/Experience/Qualifications,
                  not "the same tables and everything" duplicated). */}
              {!showAIContent && (
                <tr>
                  <td>Job Summary</td>
                  <td>{model1?.job_summary || "No data found"}</td>
                  <td>{model2?.job_summary || "No data found"}</td>
                  <td>{model3?.job_summary || "No data found"}</td>
                </tr>
              )}
              <tr>
                <td>Qualifications</td>
                <td>{(showAIContent ? model1?.ai_qualifications : model1?.qualifications) || "No data found"}</td>
                <td>{(showAIContent ? model2?.ai_qualifications : model2?.qualifications) || "No data found"}</td>
                <td>{(showAIContent ? model3?.ai_qualifications : model3?.qualifications) || "No data found"}</td>
              </tr>
              {!showAIContent && (
                <tr>
                  <td>Key Responsibilities</td>
                  <td><SectionList sections={model1?.keyResponsibilities} showAI={false} /></td>
                  <td><SectionList sections={model2?.keyResponsibilities} showAI={false} /></td>
                  <td><SectionList sections={model3?.keyResponsibilities} showAI={false} /></td>
                </tr>
              )}
              {!showAIContent && (
                <tr>
                  <td>Key Skills And Competencies</td>
                  <td><SectionList sections={model1?.keySkillsAndCompetencies} showAI={false} /></td>
                  <td><SectionList sections={model2?.keySkillsAndCompetencies} showAI={false} /></td>
                  <td><SectionList sections={model3?.keySkillsAndCompetencies} showAI={false} /></td>
                </tr>
              )}
            </tbody>
          </table>

          {!showAIContent && (
            <div className="jsc-kpi-card">
              <h3 className="jsc-panel-title">Key Performance Indicators</h3>
              <table className="jsc-table jsc-kpi-table">
                <thead>
                  <tr>
                    <th><div className="jsc-role-sub">Role Below</div><div>{model1?.minerva_job_title || "Job Title 1"}</div></th>
                    <th><div className="jsc-role-sub">Selected Role</div><div>{model2?.minerva_job_title || "Job Title 2"}</div></th>
                    <th><div className="jsc-role-sub">Role Above</div><div>{model3?.minerva_job_title || "Job Title 3"}</div></th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td><SectionList sections={model1?.kpiSections} showAI={false} /></td>
                    <td><SectionList sections={model2?.kpiSections} showAI={false} /></td>
                    <td><SectionList sections={model3?.kpiSections} showAI={false} /></td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/** Local single-select dropdown - same visual language as FilterBar's, kept
    local (not imported) so this page doesn't depend on FilterBar.jsx. */
function SingleDropdown({ label, value, placeholder, options, getLabel, onChange, disabled }) {
  const [open, setOpen] = useState(false);
  return (
    <div className={`jsc-field${disabled ? " jsc-field-disabled" : ""}`}>
      <button type="button" className="jsc-field-btn" onClick={() => !disabled && setOpen((o) => !o)} disabled={disabled}>
        <span className="jsc-field-label">{label}</span>
        <span className="jsc-field-value">{value ? getLabel(value) : placeholder}</span>
      </button>
      {open && !disabled && (
        <div className="jsc-menu" onMouseLeave={() => setOpen(false)}>
          {options.length === 0 && <div className="jsc-menu-empty">No options</div>}
          {options.map((opt) => (
            <button key={opt.id} type="button" className="jsc-menu-item" onClick={() => { onChange(opt); setOpen(false); }}>
              {getLabel(opt)}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/** Robot head icon - shown on the toggle button when currently in AI mode
    (button reads "Show Description"), matching old Minerva's fa-robot. */
function RobotIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="jsc-toggle-icon">
      <rect x="4" y="8" width="16" height="12" rx="3" />
      <path d="M12 8V4" />
      <circle cx="12" cy="3" r="1.4" />
      <circle cx="9" cy="14" r="1" />
      <circle cx="15" cy="14" r="1" />
    </svg>
  );
}

/** User icon - shown on the toggle button when currently in raw-description
    mode (button reads "Show AI Summary"), matching old Minerva's fa-user. */
function UserIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="jsc-toggle-icon">
      <circle cx="12" cy="8" r="3.4" />
      <path d="M5 20c0-3.6 3.1-6.4 7-6.4s7 2.8 7 6.4" />
    </svg>
  );
}
